import { Spin, Steps, type UploadProps } from 'antd';
import { useEffect, useState } from 'react';
import type { ApiResponseStatus } from '@/std/api/types';
import type { UploadRequestOption } from '@/types/antd';
import { Page } from '@/components/page/Page';
import { Case, Match } from '@/components/util/Match';
import { getEmpresaIdpk } from '@/features/login/util';
import { type BuildUrlParams, buildUrl } from '@/std/api/buildUrl';
import { comTokenPost } from '@/std/api/comToken';
import { fileToBase64 } from '@/std/base64';
import { formatSecondsToMinutes } from '@/std/datetime';
import { uuid } from '@/std/util';
import { Endpoint } from '@/types/enum/endpoint';
import type { UploadedFile } from './components/types';
import { StepOne, type FormImportacaoDiValues } from './components/StepOne';
import { StepThree } from './components/StepThree';
import { StepTwo } from './components/StepTwo';
import { UploadedFileStatus } from './components/UploadedFileStatus';

export const tipoImportacaoXml = ['nfe', 'di'] as const;
export type TipoImportacaoXml = (typeof tipoImportacaoXml)[number];

export function ImportacaoXML({ tipo }: { tipo: TipoImportacaoXml }) {
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const removeFileFromList = (fileName: string) => {
        setUploadedFiles((prev) => prev.filter((file) => file.file.name !== fileName));
    };

    const postXmlFile = async (
        uploadedFile: UploadedFile,
        formValue?: FormImportacaoDiValues,
    ): Promise<void> => {
        await fileToBase64(uploadedFile.file).then(async (base64) => {
            const params: BuildUrlParams = {
                empresa_idpk: getEmpresaIdpk(),
                extensao: 'xml',
            };

            if (tipo === 'di') {
                params.DI = 'S';
                params.DI_numero = formValue?.DI_numero;
                params.DI_data_desembaraco = formValue?.DI_data_desembaraco;
            }

            const url = buildUrl(Endpoint.ImportacaoNFEEmitida, params);

            const body = {
                file_base64: base64,
                file_name: uploadedFile.file.name,
            };

            await comTokenPost(url, body).then((res) => {
                if (!res.data.registros[0]) {
                    return;
                }

                const xmlStatusUpload = res.data.registros[0] as {
                    status: ApiResponseStatus;
                    mensagem: string;
                };

                setUploadedFiles((prev) =>
                    prev.map((prevUpFile) => {
                        if (prevUpFile.file.name === uploadedFile.file.name) {
                            return {
                                ...prevUpFile,
                                msg: xmlStatusUpload.mensagem,
                                status: xmlStatusUpload.status,
                            };
                        }

                        if (prevUpFile.status === 'sucesso') {
                            prevUpFile.msg = 'Pronto!';
                        }

                        return prevUpFile;
                    }),
                );
            });
        });
    };

    const proccessFiles = async (formValue?: FormImportacaoDiValues) => {
        setIsUploading(true);
        setCurrentStep(1);

        for (const file of uploadedFiles) {
            await postXmlFile(file, formValue);
        }

        setIsUploading(false);
    };

    const draggerProps: UploadProps = {
        accept: 'text/xml, .xml, xml/*',
        beforeUpload: async (file, fileList) => {
            // verificar se é o primeiro arquivo para não ficar chamando toda hora
            if (file?.name === fileList[0]?.name) {
                setLoadingFiles(true);

                // precisa de um tempo para atualizar o status e mostrar o loading
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            return new Promise((resolve) => {
                setSeconds(0);
                resolve();
            });
        },
        customRequest: (options: UploadRequestOption) => {
            const { onSuccess } = options;
            const file = options.file as File;
            const id = uuid();

            setUploadedFiles((prev) => [
                ...prev,
                {
                    file,
                    id,
                    msg: 'Aguardando processamento',
                    status: 'aguardando',
                },
            ]);

            // mudado o status para "done" para saber quando terminou de fazer o uploading
            if (onSuccess) {
                onSuccess({ status: 'done' });
            }
        },
        disabled: isUploading,
        listType: 'text',
        multiple: true,
        name: 'file',
        onChange: (info) => {
            const { fileList } = info;
            const lastFile = fileList[fileList.length - 1];

            // verifica se é o último arquivo já completo para tirar o loading
            if (lastFile?.status === 'done') {
                setLoadingFiles(false);
            }
        },
        showUploadList: false,
    };

    const finalizar = () => {
        setUploadedFiles([]);
        setSeconds(0);
        setIsUploading(false);
        history.go(-1);
        setCurrentStep(0);
    };

    useEffect(() => {
        if (isUploading) {
            const timer = setTimeout(() => {
                setSeconds((old) => old + 1);
            }, 1000);

            return () => clearTimeout(timer);
        }

        return () => null;
    }, [isUploading]);

    useEffect(() => {
        const everyXmlWasPosted = !uploadedFiles.some((uf) => uf.status === 'aguardando');

        if (everyXmlWasPosted && uploadedFiles.length) {
            setCurrentStep(2);
        }
    }, [uploadedFiles]);

    const title = 'Importação de notas fiscais';

    return (
        <Page
            path={['Fiscal', 'Notas Fiscais (NF-e)', 'Importação']}
            title={title}
            voltar={{ to: '/a/nota-fiscal' }}
        >
            <Page.Header title={title} />

            <Page.Body>
                <Steps
                    current={currentStep}
                    items={[
                        {
                            content: 'Informar XML',
                            title: 'Preparação',
                        },
                        {
                            content:
                                tipo === 'nfe'
                                    ? 'Lendo notas e importando'
                                    : 'Lendo e importando DI',
                            subTitle: `Progresso: ${formatSecondsToMinutes(seconds)} `,
                            title: 'Processamento',
                        },
                        {
                            content: 'Pronto! Processo executado',
                            title: 'Conclusão',
                        },
                    ]}
                />

                <Match>
                    <Case when={currentStep === 0}>
                        <Spin size='large' spinning={loadingFiles}>
                            <StepOne
                                disableProcessarBtn={!uploadedFiles.length}
                                draggerProps={draggerProps}
                                isUploading={isUploading}
                                proccessFiles={proccessFiles}
                                tipo={tipo}
                            />
                        </Spin>
                    </Case>
                    <Case when={currentStep === 1}>
                        <StepTwo />
                    </Case>
                    <Case when={currentStep === 2}>
                        <StepThree finalizar={finalizar} uploadedFiles={uploadedFiles} />
                    </Case>
                </Match>

                <UploadedFileStatus
                    currentStep={currentStep}
                    removeFilefromList={removeFileFromList}
                    uploadedFiles={uploadedFiles}
                />
            </Page.Body>
        </Page>
    );
}
